import Vue from "vue";
import Vuex from "vuex";
import { compact, find as _find, sumBy, omit, sortBy, groupBy,uniqBy,orderBy, sum } from "lodash";
import algoliasearch from "algoliasearch";
import moment from "moment";
// Search-only key. It ships to the browser, so never put the admin key here;
// the admin key lives only in the functions config (algolia.key).
const searchClient = algoliasearch(
  "HT133OVE5V",
  "1df05f9c8727ed17e49d3b6fb3ad4ac2"
);
const index = searchClient.initIndex("tools");

Vue.use(Vuex);

function fulfilledAmount(tool) {
  if (!tool.fulfillments) tool.fulfillments = [];
  if (!tool.fulfillments.length) {
    return tool.qtyFulfilled || 0;
  } else {
    return sumBy(tool.fulfillments, "qty");
  }
}

// Newest first. createDate is null on a job this browser just created until
// the server timestamp lands, so treat that as newest.
function byCreateDateDesc(jobSites) {
  return orderBy(
    jobSites,
    (js) =>
      js.createDate && js.createDate.toMillis
        ? js.createDate.toMillis()
        : Infinity,
    "desc"
  );
}

function incomplete(toolOrder) {
  // cycle through all and determine if all items are fulfilled
  const found = toolOrder.tools.find((t) => {
    if (!t.cancelled && !t.settled) {
      return fulfilledAmount(t) < t.qty;
    }
  });
  return !!found;
}

const store = new Vuex.Store({
  state: {
    loading: false,
    snackbar: false,
    user: null,
    cart: [],
    newCart: [],
    tools: [],
    reloadedOrder: [],
    reloadTruckRequest: null,
    jobSites: [],
    jobSitesAll: [],
    jobSitesOpenClosed: [],
    toolOrders: [],
    toolPickups: [],
    truckRequests: [],
    closedToolOrders: [],
    searchClient,
    activeTab: 0,
    employees: [],
    payroll: [],
    allEmployees: [],
    allPayroll: [],
    employeesLoaded: false,
    payrollLoaded: false,
  },
  getters: {
    user: (state) => {
      return state.user;
    },
    cart: (state) => {
      return state.tools.filter((tool) => {
        return tool.qty > 0;
      });
    },
    tools: (state) => {
      return state.tools;
    },
    toolOrdersOpen: (state) => {
      return state.toolOrders.filter((to) => !to.fulfilled);
    },
    toolOrdersFulfilled: (state) => {
      return state.toolOrders.filter((to) => to.fulfilled && !to.cancelled);
    },
    toolOrdersCancelled: (state) => {
      return state.toolOrders.filter((to) => to.cancelled);
    },
    toolOrdersIncomplete: (state) => {
      return state.toolOrders.filter((to) => {
        return (
          incomplete(to) &&
          to.fulfilled &&
          moment(to.orderDate.toDate()) > moment("2020-05-10") &&
          !to.cancelled
        );
      });
    },
    toolOrdersIssues: (state) => {
      return state.toolOrders.filter(
        (to) => to.issues && to.issues.every((i) => !i.resolved)
      );
    },
    toolOrdersIssuesResolved: (state) => {
      return state.toolOrders.filter(
        (to) => to.issues && to.issues.every((i) => i.resolved)
      );
    },
    jobSitesPm: (state) => {
      return state.jobSites.filter((j) => {
        if (j.projectManagers && j.projectManagers.length) {
          return j.projectManagers.some((pm) => pm.id == state.user.uid);
        }
      });
    },
    jobSitesPmIds: (state, getters) => {
      return getters.jobSitesPm.map((jspm) => jspm.id);
    },
    toolOrdersIssuesForPm: (state, getters) => {
      if (getters.jobSitesPm.length) {
        return getters.toolOrdersIssues.filter((i) => {
          return getters.jobSitesPmIds.includes(i.jobSite.id);
        });
      }
    },
    toolOrdersIncompleteForPm: (state, getters) => {
      if (getters.jobSitesPm.length) {
        return getters.toolOrdersIncomplete.filter((i) => {
          return getters.jobSitesPmIds.includes(i.jobSite.id);
        });
      }
    },
    toolOrdersForPm: (state, getters) => {
      if (getters.jobSitesPm.length) {
        return state.toolOrders.filter((i) => {
          return getters.jobSitesPmIds.includes(i.jobSite.id);
        });
      }
    },
    toolPickupsOpen: (state) => {
      return state.toolPickups.filter((to) => to.status == "open");
    },
    toolPickupsClosed: (state) => {
      return state.toolPickups.filter((to) => to.status == "closed");
    },
    toolPickupsCancelled: (state) => {
      return state.toolPickups.filter((to) => to.status == "cancelled");
    },
    truckRequestsOpen: (state) => {
      return state.truckRequests.filter((to) => to.status == "open");
    },
    truckRequestsScheduled: (state) => {
      return state.truckRequests.filter((to) => to.status == "scheduled");
    },
    truckRequestsClosed: (state) => {
      return state.truckRequests.filter((to) => to.status == "closed");
    },
    truckRequestsCancelled: (state) => {
      return state.truckRequests.filter((to) => to.status == "cancelled");
    },
    truckRequestsMissingDump: (state) => {
      return state.truckRequests.filter((to) => {
        return (
          to.status == "closed" &&
          !to.dumpTickets &&
          to.requestType !== "Mini Pickup/Dropoff" &&
          to.pickupType !== "Drop Off Container"
        );
      });
    },
  },
  mutations: {
    loading(state, status) {
      state.loading = status;
    },
    snackbar(state, status) {
      state.snackbar = status;
    },
    cacheUser(state, user) {
      state.user = user;
    },
    updateToolList(state, tools) {
      state.tools = tools;
    },
    plusQty(state, id) {
      state.tools.map((t) => {
        if (t.id == id) {
          t.qty++;
        }
        return t;
      });
    },
    minusQty(state, id) {
      state.tools.map((t) => {
        if (t.id == id) {
          t.qty--;
        }
        return t;
      });
    },
    newPlusQty(state, id) {
      state.newCart.map((t) => {
        if (t.id == id) {
          t.qty++;
        }
        return t;
      });
    },
    newMinusQty(state, id) {
      state.newCart.map((t) => {
        if (t.id == id) {
          t.qty--;
        }
        return t;
      });
    },
    resetToolQty(state) {
      state.tools.forEach((t) => {
        t.qty = 0;
      });
      localStorage.removeItem("cartItems");
    },
    newResetToolQty(state) {
      state.newCart = [];
      localStorage.removeItem("cart");
    },
    reloadedOrder(state, tools) {
      state.reloadedOrder = tools;
    },
    reloadTruckRequest(state, request) {
      state.reloadTruckRequest = request;
    },
    clearReloadedTruckRequest(state) {
      state.reloadTruckRequest = null;
    },
    clearReloadedOrder(state) {
      state.reloadedOrder = [];
    },
    jobSites(state, jobSites) {
      state.jobSites = jobSites;
    },
    jobSitesAll(state, jobSites) {
      state.jobSitesAll = jobSites;
    },
    jobSitesOpenClosed(state, jobSites) {
      state.jobSitesOpenClosed = jobSites;
    },
    toolOrders(state, toolOrders) {
      state.toolOrders = toolOrders;
    },
    toolPickups(state, toolPickups) {
      state.toolPickups = toolPickups;
    },
    closedToolOrders(state, closedToolOrders) {
      state.closedToolOrders = closedToolOrders;
    },
    truckRequests(state, truckRequests) {
      state.truckRequests = truckRequests;
    },
    onBlur() {
      // do nothing. This is to trigger the local storage on blur.
    },
    newOnBlur() {
      // do nothing. This is to trigger the local storage on blur.
    },
    removeCartItem(state, id) {
      state.tools.forEach((t) => {
        if (t.id == id) {
          t.qty = 0;
        }
      });
    },
    addToCart(state, item) {
      item.id = item.objectID;
      if (state.newCart.find((i) => item.id == i.id)) {
        state.newCart.map((i) => {
          if (item.id == i.id) {
            i.qty = i.qty + item.qty;
            return i;
          } else {
            return i;
          }
        });
      } else {
        item = omit(item, ["_highlightResult", "__position"]);
        state.newCart.push(item);
      }
    },
    removeNewCartItem(state, itemId) {
      state.newCart = state.newCart.filter((c) => c.id !== itemId);
    },
    updateActiveTab(state, tabIndex) {
      state.activeTab = tabIndex;
    },
    employees(state, employees) {
      state.employees = employees;
    },
    payroll(state, payroll) {
      state.payroll = payroll;
    },
    allEmployees(state, employees) {
      state.allEmployees = employees;
    },
    allPayroll(state, payroll) {
      state.allPayroll = payroll;
    },
    employeesLoaded(state, loaded) {
      state.employeesLoaded = loaded;
    },
    payrollLoaded(state, loaded) {
      state.payrollLoaded = loaded;
    },
  },
  actions: {
    // Open Alba/Volk jobs only. External truck request sites are carting
    // clients, not jobs, and only the truck request form needs them (see
    // getJobSitesAll). Filtering on the `external` flag server side keeps
    // ~2,600 external sites out of every user's download (#108). Equality
    // filters only, so no composite index is needed; sorted here instead.
    async getJobSites({ commit }, fb) {
      try {
        await fb.db
          .collection("jobSites")
          .where("status", "==", "open")
          .where("external", "==", false)
          .onSnapshot((querySnapshot) => {
            let jobSites = [];
            querySnapshot.forEach(function(doc) {
              jobSites.push({
                id: doc.id,
                ...doc.data(),
              });
            });
            commit("jobSites", byCreateDateDesc(jobSites));
          });
      } catch (err) {
        console.log(err);
      }
    },
    // Open and closed Alba/Volk jobs, for Payroll and Employee Detail.
    async getJobSitesOpenClosed({ commit }, fb) {
      try {
        await fb.db
          .collection("jobSites")
          .where("external", "==", false)
          .onSnapshot((querySnapshot) => {
            let jobSites = [];
            querySnapshot.forEach(function(doc) {
              jobSites.push({
                id: doc.id,
                ...doc.data(),
              });
            });
            commit("jobSitesOpenClosed", byCreateDateDesc(jobSites));
          });
      } catch (err) {
        console.log(err);
      }
    },
    // Every open job site, external truck request sites included. Only the
    // carting side needs this: JobSelectorAll on the truck request form and
    // the Reports page. Loaded on demand, never at login.
    async getJobSitesAll({ commit }, fb) {
      return new Promise(async(resolve, reject) => {
        try {
          await fb.db
            .collection("jobSites")
            .where("status", "==", "open")
            .orderBy("createDate", "desc")
            .onSnapshot((querySnapshot) => {
              let jobSites = [];
              querySnapshot.forEach((doc) => {
                jobSites.push({
                  id: doc.id,
                  ...doc.data(),
                });
              });
              commit("jobSitesAll", jobSites);
              resolve();
            });
        } catch (err) {
          console.log(err);
          reject();
        }
      });
    },
    async getToolOrders({ commit }, fb) {
      try {
        console.log("getting tool orders");

        await fb.db
          .collection("toolOrders")
          .where("status", "==", "open")
          .orderBy("orderDate", "desc")
          .limit(200)
          .onSnapshot((querySnapshot) => {
            var toolOrders = [];
            querySnapshot.forEach(function(doc) {
              toolOrders.push({
                id: doc.id,
                fullName: `${doc.data().orderedBy.firstName} ${
                  doc.data().orderedBy.lastName
                }`,
                ...doc.data(),
              });
            });
            commit("toolOrders", toolOrders);
          });
      } catch (err) {
        console.log(err);
      }
    },
    async getToolPickups({ commit }, fb) {
      try {
        await fb.db
          .collection("toolPickups")
          .orderBy("orderDate", "desc")
          .limit(300)
          .onSnapshot((querySnapshot) => {
            var toolPickups = [];
            querySnapshot.forEach(function(doc) {
              toolPickups.push({
                id: doc.id,
                fullName: `${doc.data().orderedBy.firstName} ${
                  doc.data().orderedBy.lastName
                }`,
                ...doc.data(),
              });
            });
            commit("toolPickups", toolPickups);
          });
      } catch (err) {
        console.log(err);
      }
    },
    async getTruckRequests({ commit }, fb) {
      try {
        await fb.db
          .collection("truckRequests")
          .orderBy("orderDate", "desc")
          .limit(1000)
          .onSnapshot((querySnapshot) => {
            var truckRequests = [];
            querySnapshot.forEach(function(doc) {
              truckRequests.push({
                id: doc.id,
                fullName: `${doc.data().orderedBy.firstName} ${
                  doc.data().orderedBy.lastName
                }`,
                ...doc.data(),
              });
            });
            truckRequests.forEach((t) => {
              if (!t.draftTime) t.draftTime = t.requestTime || "";
              if (!t.draftDuration) t.draftDuration = t.duration || "";
            });
            truckRequests = sortBy(truckRequests, "requestDate");
            // console.info('truckRequests',truckRequests)
            commit("truckRequests", truckRequests);
          });
      } catch (err) {
        console.log(err);
      }
    },
    async getClosedToolOrders({ commit }, fb) {
      try {
        const toolOrders = await fb.db
          .collection("toolOrders")
          .where("status", "==", "closed")
          .orderBy("closeDate", "desc")
          .limit(100)
          .get();
        let closedOrders = [];
        toolOrders.forEach((u) => {
          closedOrders.push({
            id: u.id,
            fullName: `${u.data().orderedBy.firstName} ${
              u.data().orderedBy.lastName
            }`,
            ...u.data(),
          });
        });
        commit("closedToolOrders", closedOrders);
      } catch (err) {
        console.log(err);
      }
    },
    async reloadToolsFromPreviousOrder({ commit, state }) {
      // clear tools in cart if any
      commit("newResetToolQty");
      // fetch tools from index by id
      let { results } = await index.getObjects(
        state.reloadedOrder.map((t) => t.id)
      );
      results = compact(results); //remove nulls if id's are not found
      // loop and add tools to cart with matching qty
      results.forEach((t) => {
        t.qty = _find(state.reloadedOrder, { id: t.objectID }).qty;
        commit("addToCart", t);
      });
      // clear the tools in reloaded order
      commit("clearReloadedOrder");
    },
    async loadToolsFromLocal({ commit, state }) {
      if (localStorage.getItem("cart")) {
        const cartItems = JSON.parse(localStorage.getItem("cart"));
        const { results } = await index.getObjects(
          cartItems.map((ci) => ci.id)
        );
        // convert ObjectID to id
        const tools = results.map((r) => {
          return { id: r.objectID, ...r };
        });
        tools.forEach((t) => {
          const found = cartItems.find((ci) => ci.id == t.id);
          const foundInCart = state.newCart.find((ci) => ci.id == t.id);
          if (found && !foundInCart) {
            t.qty = found.qty;
            commit("addToCart", t);
          }
        });
      }
    },
    async getToolsFromIndex({ commit }, toolIds) {
      let { results } = await index.getObjects(toolIds);
      results = compact(results);
      return results;
    },
    async duplicateTruckRequest({ commit }, payload) {
      const fb = payload.fb;
      const dupe = payload.dupe;
      // Push a duplicate's clock times 30 minutes later than the source so
      // same-day duplicates land in their own scheduler slot instead of
      // colliding on the grid (drivers duplicate without picking a time).
      // Applied to both scheduledTime (the slot the grid/print key off) and
      // requestTime (the label shown on the grid card) so they stay in sync.
      // Invalid/empty times pass through unchanged.
      const bumpTime = (t) =>
        t && moment(t, "h:mm A").isValid()
          ? moment(t, "h:mm A")
              .add(30, "minutes")
              .format("h:mm A")
          : t || "";
      return await fb.db.runTransaction(async (transaction) => {
        // Get the metadata document and increment the count.
        const metaRef = fb.db.doc("metadata/truckRequests");
        const metaData = (await transaction.get(metaRef)).data() || {};

        const number = metaData.count + 1;

        transaction.update(metaRef, {
          count: number,
        });

        // Update the order document
        const requestRef = fb.db
          .collection("truckRequests")
          .doc(number.toString());

        transaction.set(requestRef, {
          jobSite: dupe.jobSite,
          requestDate: dupe.requestDate,
          requestDateFormatted: dupe.requestDateFormatted,
          status: "scheduled",
          duration: dupe.duration,
          orderedBy: dupe.orderedBy,
          orderDate: fb.timestamp,
          orderNumber: number.toString(),
          requestType: dupe.requestType,
          numberOfTrucks: dupe.numberOfTrucks,
          pickupType: dupe.pickupType,
          materialType: dupe.materialType,
          waitTime: dupe.waitTime,
          qtyType: dupe.qtyType,
          qty: dupe.qty,
          requestTime: bumpTime(dupe.requestTime),
          fullLoad: dupe.fullLoad,
          fullLoadsQty: dupe.fullLoadsQty,
          miniDropoffQty: dupe.miniDropoffQty,
          miniPickupQty: dupe.miniPickupQty,
          notes: dupe.notes,
          scheduledBy: dupe.scheduledBy || null,
          scheduledDate: dupe.scheduledDate || null,
          scheduledTime: bumpTime(dupe.scheduledTime),
          scheduledDuration: dupe.scheduledDuration || "",
          scheduledDriver: dupe.scheduledDriver || null,
          scheduledDay: dupe.scheduledDay || null,
          truckManagerNotes: dupe.truckManagerNotes || "",
          duplicatedFrom: dupe.orderNumber,
        });
      });
    },
    async getEmployees({ commit }, fb) {
      try {
        await fb.db
          .collection("employees")
          .orderBy("lastName", "asc")
          .onSnapshot((querySnapshot) => {
            let employees = [];
            querySnapshot.forEach(function(doc) {
              const data = doc.data()
              if(!data.terminateDate){
                employees.push({
                  docId: doc.id,
                  ...data,
                });
              }
            });
            commit("employees", employees);
          });
      } catch (err) {
        console.log(err);
      }
    },
    async getAllEmployees({ commit }, fb) {
      try {
        await fb.db
          .collection("employees")
          .orderBy("lastName", "asc")
          .onSnapshot((querySnapshot) => {
            let employees = [];
            querySnapshot.forEach(function(doc) {
              const data = doc.data();
              employees.push({
                docId: doc.id,
                ...data,
              });
            });
            commit("allEmployees", employees);
            commit("employeesLoaded", true);
          });
      } catch (err) {
        console.log(err);
        commit("employeesLoaded", false);
      }
    },
    async getPayroll({ commit }, fb) {
      try {
        await fb.db.collection("payroll").onSnapshot((querySnapshot) => {
          let payroll = [];
          querySnapshot.forEach(function(doc) {
            payroll.push({
              docId: doc.id,
              ...doc.data(),
            });
          });
          commit("payroll", payroll);
        });
      } catch (err) {
        console.log(err);
      }
    },
    async getAllPayroll({ commit }, fb) {
      try {
        await fb.db.collection("payroll").onSnapshot((querySnapshot) => {
          let payroll = [];
          querySnapshot.forEach(function(doc) {
            payroll.push({
              docId: doc.id,
              ...doc.data(),
            });
          });
          commit("allPayroll", payroll);
          commit("payrollLoaded", true);
        });
      } catch (err) {
        console.log(err);
        commit("payrollLoaded", false);
      }
    },
    async manDaysCount(_commit, { fb, jobId }) {
      try {
        const workersRes = await fb.db
          .collection("punchRecords")
          .where("direction", "==", "in")
          .where("jobSite", "==", jobId)
          .get();
        let punchRecords = [];
        workersRes.forEach(function(doc) {
          punchRecords.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        punchRecords = punchRecords.map((pr) => {
          return { day: moment(pr.time.toDate()).format('YYYY-MM-DD') };
        });
        const groupedByDay = groupBy(punchRecords, 'day')
        return sum(Object.values(groupedByDay).map(w=>w.length));
      } catch (err) {
        console.log(err);
      }
    },
    async getManDays(_commit, { fb, jobId }) {
      try {
        const workersRes = await fb.db
          .collection("punchRecords")
          .where("direction", "==", "in")
          .where("jobSite", "==", jobId)
          .get();

        let punchRecords = [];
        workersRes.forEach(function(doc) {
          punchRecords.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        const employeesRes = await fb.db.collection("employees").get();
        let employees = [];
        employeesRes.forEach(function(doc) {
          employees.push({
            id: doc.id,
            ...doc.data(),
            name: doc.data().firstName + ' ' + doc.data().lastName
          });
        });
        punchRecords = punchRecords.map((pr) => {
          return { ...employees.find((e) => e.id === pr.employee), day: moment(pr.time.toDate()).format('YYYY-MM-DD'),...pr };
        });
        const groupedByDay = groupBy(punchRecords, 'day')
        const workersSummary = {
            count: sum(Object.values(groupedByDay).map(w=>w.length)),
            punchRecords: orderBy(punchRecords,'time', 'desc'),
            departments: groupBy(uniqBy(punchRecords, "employee"), "department"),
          };
        return workersSummary;
      } catch (err) {
        console.log(err);
      }
    },
  },
});

store.subscribe((mutation, state) => {
  if (
    mutation.type === "newMinusQty" ||
    mutation.type === "newPlusQty" ||
    mutation.type === "newOnBlur" ||
    mutation.type === "removeNewCartItem" ||
    mutation.type === "addToCart"
  ) {
    const cartItems = state.newCart.map((t) => {
      return { id: t.id, qty: t.qty };
    });

    localStorage.setItem("cart", JSON.stringify(cartItems));
  } else if (mutation.type === "newResetToolQty") {
    localStorage.removeItem("cart");
  }
});

export default store;
